import React, { useState } from 'react'
import { Column, ColumnContainer, Inline, MangoButton, TextStyle } from '@intellihr/blueberry'
import { useScope } from 'src/services/i18n/LocalizationProvider'
import {
  IFormChildProps,
  IRuleSet,
  ReduxForm,
  ReduxVerticalFormField,
  useValidationRules,
} from 'src/services/reduxForm'
import { SingleDateInputV2, TextAreaInput, TextInput, VerticalForm } from '@intellihr/ui-components'
import { Field } from 'redux-form'
import { ExpiryVisibilityTypeIds } from 'src/services/constants/qualifications/expiryVisibilityTypeIds'
import { RegistrationNumberVisibilityTypeIds } from 'src/services/constants/qualifications/registrationNumberVisibilityTypeIds'
import { PersonDocument } from 'src/__gql__/globalTypes'
import { useToasts } from 'src/services/toasts'
import { AttachmentRequirementTypeIds } from 'src/services/constants/qualifications/attachmentRequirementTypeIds'
import { ResponsiveAlign } from '@intellihr/blueberry/dist/_utils/alignmentProps'
import { QualificationInstanceAttachment } from './QualificationInstanceAttachment'
import { IQualificationInstanceComment } from '../../Contents/ShowQualificationInstancePage/ShowQualificationInstancePageContent'
import { QualificationInstanceAttachmentForOnboarding } from './QualificationInstanceAttachmentForOnboarding'

interface IQualificationInstance {
  id: string
  issuingOrganisation?: string | null
  registrationNumber?: string | null
  issueDate?: string | null
  expiryDate?: string | null
  notes?: string | null
  qualificationTemplate: ISlimQualificationTemplate
  attachments: PersonDocument[]
}

interface IQualificationInstanceWithComments extends IQualificationInstance {
  comments: IQualificationInstanceComment[]
}

interface ISlimQualificationTemplate {
  id: string
  name?: string
  qualificationAttachmentRequirementType: { id: string }
  qualificationExpiryType: { id: string }
  qualificationRegistrationNumberVisibilityType: { id: string }
  registrationNumberName?: string | null
}

interface IQualificationInstanceFormProps {
  onSubmit: (formData: IOnboardingFormSubmitData, saveAsDraft?: boolean) => void
  submitting: boolean
  qualificationInstance: IQualificationInstance
  submitButtonLabel?: string
  showDraftButton?: boolean
  submitButtonAlignment?: ResponsiveAlign
  forOnboarding?: boolean
}

interface IOnboardingFormSubmitData {
  qualificationInstanceId: string
  issuingOrganisation: string
  registrationNumber: string | null
  issueDate: DateTime
  expiryDate: DateTime | null
  notes: string | null
}

const observedFields = new Set<string>([
  'issuingOrganisation',
  'registrationNumber',
  'issueDate',
  'expiryDate',
  'notes',
])

const QualificationInstanceForm: React.FC<IQualificationInstanceFormProps> = ({
  onSubmit,
  submitting,
  qualificationInstance,
  submitButtonLabel,
  showDraftButton = false,
  submitButtonAlignment = 'left',
  forOnboarding = false,
}) => {
  const formName = 'qualification-form'
  const tCommon = useScope('common:components')
  const tFeature = useScope('qualifications:components.QualificationInstancePage')
  const postToast = useToasts()
  const [attachments, setAttachments] = useState<PersonDocument[]>(qualificationInstance.attachments ?? [])
  const [attachmentUploading, setAttachmentUploading] = useState<boolean>(false)

  const validationRules = () => {
    const rules: IRuleSet[] = [
      {
        field: 'issueDate',
        rule: 'required',
      },
      {
        field: 'issuingOrganisation',
        rule: 'required',
      },
    ]

    if (
      qualificationInstance?.qualificationTemplate.qualificationExpiryType.id ===
      ExpiryVisibilityTypeIds.expiryDateRequired
    ) {
      rules.push({
        field: 'expiryDate',
        rule: 'required',
      })
    }

    if (
      qualificationInstance?.qualificationTemplate.qualificationRegistrationNumberVisibilityType.id ===
      RegistrationNumberVisibilityTypeIds.registrationNumberRequired
    ) {
      rules.push({
        field: 'registrationNumber',
        rule: 'required',
      })
    }

    return rules
  }

  const validate = useValidationRules(validationRules)

  const handleSubmit = (formData: IOnboardingFormSubmitData, saveAsDraft = false) => {
    // Validate that expiry date is after the issue date
    if (formData.expiryDate && formData.expiryDate <= formData.issueDate) {
      postToast({
        type: 'alert',
        content: tFeature('validation.errorExpiryDateMustBeAfterIssueDate'),
        logType: 'error',
      })

      return
    }

    // Validate attachments here since we can't validate them on the form itself
    // We only do this check if it's not a draft
    if (
      !saveAsDraft &&
      qualificationInstance.qualificationTemplate.qualificationAttachmentRequirementType.id ===
        AttachmentRequirementTypeIds.attachmentRequired &&
      attachments.length === 0
    ) {
      postToast({
        type: 'alert',
        content: tFeature('validation.errorAtLeastOneAttachmentRequired'),
        logType: 'error',
      })

      return
    }
    onSubmit(formData, saveAsDraft)
  }

  // Wrapper around handleSubmit that the form will use as a submit action to help differentiate between submitting the qual or saving as a draft
  const handleFormSubmit = (formData: IOnboardingFormSubmitData) => {
    handleSubmit(formData, false)
  }

  return (
    <ReduxForm
      form={formName}
      formComponent={VerticalForm}
      destroyOnUnmount={true}
      onSubmit={handleFormSubmit}
      validate={validate}
      observedFields={observedFields}
      initialValues={{
        issueDate: qualificationInstance.issueDate,
        expiryDate: qualificationInstance.expiryDate,
        issuingOrganisation: qualificationInstance.issuingOrganisation,
        registrationNumber: qualificationInstance.registrationNumber,
        notes: qualificationInstance.notes,
      }}
    >
      {({ observedFieldValues }: IFormChildProps<IOnboardingFormSubmitData>) => {
        return (
          <>
            <TextStyle variant="strong">{tCommon('headers.details')}</TextStyle>
            <div style={{ marginTop: '10px' }}>
              <ColumnContainer>
                <Column>
                  <Field
                    name="issuingOrganisation"
                    component={ReduxVerticalFormField}
                    props={{
                      label: tFeature('input.issuingOrganisation'),
                      component: TextInput,
                      isRequired: true,
                      componentContext: 'issuing-organisation-input',
                    }}
                  />
                </Column>
                {qualificationInstance?.qualificationTemplate.qualificationRegistrationNumberVisibilityType.id !==
                RegistrationNumberVisibilityTypeIds.noRegistrationNumber ? (
                  <Column>
                    <Field
                      name="registrationNumber"
                      component={ReduxVerticalFormField}
                      props={{
                        label:
                          // here to prevent the date picker from opening on qualification instance modal,
                          // the empty string was causing issues for certain qualification library items
                          qualificationInstance.qualificationTemplate.registrationNumberName &&
                          qualificationInstance.qualificationTemplate.registrationNumberName.trim() !== ''
                            ? qualificationInstance.qualificationTemplate.registrationNumberName
                            : tFeature('input.registrationNumber'),
                        isRequired:
                          qualificationInstance.qualificationTemplate.qualificationRegistrationNumberVisibilityType
                            .id === RegistrationNumberVisibilityTypeIds.registrationNumberRequired,
                        component: TextInput,
                        componentContext: 'registration-number-input',
                      }}
                    />
                  </Column>
                ) : null}
              </ColumnContainer>
              <ColumnContainer>
                <Column>
                  <Field
                    name="issueDate"
                    component={ReduxVerticalFormField}
                    props={{
                      label: tFeature('input.validFrom'),
                      component: SingleDateInputV2,
                      componentContext: 'valid-from-single-date-input',
                      isRequired: true,
                      inputProps: {
                        autoFocus: false,
                      },
                    }}
                  />
                </Column>
                {qualificationInstance.qualificationTemplate.qualificationExpiryType.id !==
                ExpiryVisibilityTypeIds.noExpiryDate ? (
                  <Column>
                    <Field
                      name="expiryDate"
                      component={ReduxVerticalFormField}
                      props={{
                        label: tFeature('input.validTo'),
                        component: SingleDateInputV2,
                        componentContext: 'valid-to-single-date-input',
                        isRequired:
                          qualificationInstance.qualificationTemplate.qualificationExpiryType.id ===
                          ExpiryVisibilityTypeIds.expiryDateRequired,
                        inputProps: {
                          autoFocus: false,
                        },
                        tooltipMessage: tFeature('input.validToTooltipMessage'),
                      }}
                    />
                  </Column>
                ) : null}
              </ColumnContainer>
            </div>
            <Field
              name="notes"
              component={ReduxVerticalFormField}
              props={{
                label: tFeature('input.notes'),
                component: TextAreaInput,
                componentContext: 'note-input',
              }}
            />
            {forOnboarding ? (
              <QualificationInstanceAttachmentForOnboarding
                qualificationInstanceId={qualificationInstance.id}
                attachments={attachments}
                setAttachments={setAttachments}
                setAttachmentUploading={setAttachmentUploading}
              />
            ) : (
              <QualificationInstanceAttachment
                qualificationInstanceId={qualificationInstance.id}
                attachments={attachments}
                setAttachments={setAttachments}
                setAttachmentUploading={setAttachmentUploading}
              />
            )}
            <Inline align={submitButtonAlignment}>
              <MangoButton
                variant="primary"
                type="submit"
                loading={submitting || attachmentUploading}
                disabled={submitting || attachmentUploading}
              >
                {submitButtonLabel ?? tCommon('buttons.submit')}
              </MangoButton>
              {showDraftButton && (
                <MangoButton
                  variant="secondary"
                  onClick={() => {
                    handleSubmit(observedFieldValues, true)
                  }}
                  loading={submitting || attachmentUploading}
                  disabled={submitting || attachmentUploading}
                >
                  {tFeature('input.saveDraftButton')}
                </MangoButton>
              )}
            </Inline>
          </>
        )
      }}
    </ReduxForm>
  )
}

export type {
  IOnboardingFormSubmitData,
  IQualificationInstance,
  IQualificationInstanceFormProps,
  IQualificationInstanceWithComments,
}
export { QualificationInstanceForm }
